import React, { useEffect, useMemo, useState } from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import Button from '@amzn/awsui-components-console/button';
import Header from '@amzn/awsui-components-console/header';
import Link from '@amzn/awsui-components-console/link';
import Pagination from '@amzn/awsui-components-console/pagination';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Table, { TableProps } from '@amzn/awsui-components-console/table';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import {
  DraftState,
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
  StringInfo,
  TranslationStatus,
} from '../types';
import { MAX_STRINGS_PER_GROUP } from '../constants';
import { sortItems } from 'src/common/utils/sorting';
import styles from '../StringGroupDetail.module.scss';
import {
  pageSizeOf,
  TablePreferences,
  useTablePreferences,
} from 'src/common/ui/TablePreferences';
import {
  STRINGS_DEFAULT_PAGE_SIZE,
  STRINGS_TABLE_PREFERENCES,
} from './stringsTablePreferences';

interface StringsTableProps {
  strings: StringInfo[];
  clientId: string;
  /** Namespace of the group — used to build the string detail links. */
  namespaceId: string;
  /** Group these strings belong to — used to build the string detail links. */
  stringGroupId: string;
  draftState: DraftState | null;
  /**
   * When false, editing controls (Create String, per-row Edit) are disabled
   * (grayed out) because the group is owned by a non-LangHub client.
   */
  canEdit?: boolean;

  /**
   * Whether the permission answer is still in flight. Gated controls show a skeleton rather
   * than a disabled state, so they never claim a missing permission before it is known.
   */
  permissionsLoading?: boolean;
  onCreateClick?: () => void;
  onEditClick?: (stringInfo: StringInfo) => void;
}

/**
 * Represents either a parent string row or an expanded variant row in the
 * flattened table model.
 */
interface TableRow {
  /** Unique key for React rendering. */
  id: string;
  /** The parent StringInfo (always present). */
  stringInfo: StringInfo;
  /** Whether this row is a variant sub-row. */
  isVariant: boolean;
  /** For variant rows, the CX ID. */
  variantCxId?: string;
  /** For variant rows, the overridden source text. */
  variantSourceText?: string;
  /** For variant rows, the overridden context note. */
  variantContextNote?: string;
  /**
   * For variant rows, whether this exact cxId is present in the SERVER's
   * variant list for this string — the only case where the variant detail page
   * has something to show.
   *
   * `DescribeString` falls back to the base string when the requested cxId has
   * no override rather than 404ing, so linking a variant the server has never
   * seen produces a link that appears to work but renders the base string. The
   * parent's `draftStatus` cannot answer this: a variant added to an existing
   * (`edited`) string sits under a parent that is itself perfectly linkable.
   */
  isVariantLinkable?: boolean;
  /** Draft status for visual indicators. */
  draftStatus?: 'added' | 'edited' | 'deleted';
}

/** Converts a DraftStringInput to a StringInfo for table display. */
function draftToStringInfo(
  draft: DraftStringInput,
  clientId: string,
): StringInfo {
  return {
    stringId: draft.stringId,
    sourceLocale: draft.sourceLocale,
    sourceText: draft.sourceText,
    contextNote: draft.contextNote,
    inlineMarkupType: draft.inlineMarkupType ?? InlineMarkupType.PlainText,
    messageFormatType: draft.messageFormatType ?? MessageFormatType.None,
    clientId,
    variants: draft.variants ?? [],
    translationStatus: TranslationStatus.Complete,
  };
}

/** Numeric priority for status sorting (lower = higher priority). */
const STATUS_SORT_PRIORITY: Record<string, number> = {
  added: 0,
  edited: 1,
  deleted: 2,
};

const TRANSLATION_STATUS_PRIORITY: Record<string, number> = {
  [TranslationStatus.Failed]: 3,
  [TranslationStatus.InProgress]: 4,
  [TranslationStatus.Cancelled]: 5,
  [TranslationStatus.Complete]: 6,
};

type MergedStringItem = {
  stringInfo: StringInfo;
  draftStatus?: 'added' | 'edited' | 'deleted';
};

/** Accessor used by `sortItems` to extract comparable values per field. */
export function sortAccessor(
  item: MergedStringItem,
  field: string,
): string | number | undefined {
  switch (field) {
    case 'stringId':
      return item.stringInfo.stringId;
    case 'status':
      return item.draftStatus
        ? STATUS_SORT_PRIORITY[item.draftStatus]
        : (TRANSLATION_STATUS_PRIORITY[item.stringInfo.translationStatus] ?? 6);
    case 'sourceText':
      return item.stringInfo.sourceText;
    default:
      // If you add a new sortingField to columnDefinitions, add a case here.
      return undefined;
  }
}

const StringsTable: React.FC<StringsTableProps> = ({
  strings,
  clientId,
  namespaceId,
  stringGroupId,
  draftState,
  canEdit = true,
  permissionsLoading = false,
  onCreateClick,
  onEditClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Client-side paging. The clamp effect below only catches an out-of-range page;
  // a size change also has to return to page 1 or the user's rows move under them.
  const { preferences, onConfirm, columnDisplay } = useTablePreferences(
    STRINGS_TABLE_PREFERENCES,
    { onPageSizeChange: () => setCurrentPage(1) },
  );
  const pageSize = pageSizeOf(preferences, STRINGS_DEFAULT_PAGE_SIZE);
  const [expandedStringIds, setExpandedStringIds] = useState<Set<string>>(
    new Set(),
  );

  // Sorting state managed manually (useCollection not suitable for this table)
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<TableRow> | undefined
  >(undefined);
  const [sortingDescending, setSortingDescending] = useState(false);

  // Build the merged string list: server strings + draft additions
  const mergedStrings = useMemo(() => {
    const result: MergedStringItem[] = [];

    const deletedSet = new Set(draftState?.deleted ?? []);
    const editedMap = new Map(
      (draftState?.edited ?? []).map((e) => [e.stringId, e]),
    );
    const addedMap = new Map(
      (draftState?.added ?? []).map((a) => [a.stringId, a]),
    );

    // Track which stringIds from server data overlap with draft-added strings.
    // This prevents duplicates when the Apollo cache already contains strings
    // that were optimistically appended (e.g. via applyDraft) while the draft
    // hasn't been cleared yet.
    const seenServerStringIds = new Set<string>();

    // Server strings with draft annotations
    for (const str of strings) {
      seenServerStringIds.add(str.stringId);
      if (deletedSet.has(str.stringId)) {
        result.push({ stringInfo: str, draftStatus: 'deleted' });
      } else if (addedMap.has(str.stringId)) {
        // String already exists in server data AND is in draft.added — show it
        // as 'added' (draft wins) but don't append it again below.
        const draftAdd = addedMap.get(str.stringId)!;
        const mergedInfo: StringInfo = {
          ...str,
          sourceText: draftAdd.sourceText,
          contextNote: draftAdd.contextNote,
          inlineMarkupType: draftAdd.inlineMarkupType,
          messageFormatType: draftAdd.messageFormatType,
          variants: draftAdd.variants ?? str.variants,
        };
        result.push({ stringInfo: mergedInfo, draftStatus: 'added' });
      } else if (editedMap.has(str.stringId)) {
        // Merge draft-edited values into the display so the table shows updated text
        const draftEdit = editedMap.get(str.stringId)!;
        const mergedInfo: StringInfo = {
          ...str,
          sourceText: draftEdit.sourceText,
          contextNote: draftEdit.contextNote,
          inlineMarkupType: draftEdit.inlineMarkupType,
          messageFormatType: draftEdit.messageFormatType,
          variants: draftEdit.variants ?? str.variants,
        };
        result.push({ stringInfo: mergedInfo, draftStatus: 'edited' });
      } else {
        result.push({ stringInfo: str });
      }
    }

    // Append newly added strings from draft that are NOT already in server data
    for (const added of draftState?.added ?? []) {
      if (!seenServerStringIds.has(added.stringId)) {
        result.push({
          stringInfo: draftToStringInfo(added, clientId),
          draftStatus: 'added',
        });
      }
    }

    // Surface unsubmitted ("New") strings at the top so they're visible on the
    // first page instead of being buried after the published strings — they're
    // the rows the user is actively working on. Stable partition keeps the
    // relative order within each group.
    return [
      ...result.filter((item) => item.draftStatus === 'added'),
      ...result.filter((item) => item.draftStatus !== 'added'),
    ];
  }, [strings, draftState, clientId]);

  const effectiveTotalCount = mergedStrings.length;

  /**
   * Each string's variant CX IDs *as the server reports them*, keyed by stringId.
   *
   * Deliberately sourced from the unmerged `strings` prop rather than from
   * `mergedStrings`: the merge folds the draft's pending variants into
   * `stringInfo.variants`, so it can no longer distinguish a variant the server
   * knows from one that exists only in the local draft. A string absent from
   * this map has no server entry at all (draft-added), so none of its variants
   * are viewable.
   */
  const serverVariantCxIds = useMemo(() => {
    const byStringId = new Map<string, Set<string>>();
    for (const str of strings) {
      byStringId.set(str.stringId, new Set(str.variants.map((v) => v.cxId)));
    }
    return byStringId;
  }, [strings]);

  // Apply sorting to merged list. When the user explicitly sorts, all items
  // sort together. When no sort is active, the default ordering (added first)
  // is preserved from mergedStrings.
  const sortedStrings = useMemo(() => {
    if (!sortingColumn?.sortingField) return mergedStrings;
    return sortItems(
      mergedStrings,
      sortingColumn.sortingField,
      sortingDescending,
      sortAccessor,
    );
  }, [mergedStrings, sortingColumn, sortingDescending]);

  // Client-side pagination over sorted list
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedStrings.slice(start, start + pageSize);
  }, [sortedStrings, currentPage, pageSize]);

  // Variant sub-rows are inserted below their parent after sorting, so they
  // stay grouped regardless of sort order.
  const tableRows: TableRow[] = useMemo(() => {
    const rows: TableRow[] = [];
    for (const item of paginatedItems) {
      rows.push({
        id: item.stringInfo.stringId,
        stringInfo: item.stringInfo,
        isVariant: false,
        draftStatus: item.draftStatus,
      });
      if (expandedStringIds.has(item.stringInfo.stringId)) {
        // Resolved once per parent, then asked per variant — the answer differs
        // between the sub-rows of a single string (a draft-added variant sits
        // alongside the string's server variants).
        const serverCxIds = serverVariantCxIds.get(item.stringInfo.stringId);
        for (const variant of item.stringInfo.variants) {
          rows.push({
            id: `${item.stringInfo.stringId}__variant__${variant.cxId}`,
            stringInfo: item.stringInfo,
            isVariant: true,
            variantCxId: variant.cxId,
            variantSourceText: variant.sourceText,
            variantContextNote: variant.contextNote,
            isVariantLinkable: serverCxIds?.has(variant.cxId) ?? false,
            draftStatus: item.draftStatus,
          });
        }
      }
    }
    return rows;
  }, [paginatedItems, expandedStringIds, serverVariantCxIds]);

  const toggleExpand = (stringId: string) => {
    setExpandedStringIds((prev) => {
      const next = new Set(prev);
      if (next.has(stringId)) {
        next.delete(stringId);
      } else {
        next.add(stringId);
      }
      return next;
    });
  };

  const handleSortingChange = ({
    detail,
  }: {
    detail: TableProps.SortingState<TableRow>;
  }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(detail.isDescending ?? false);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(effectiveTotalCount / pageSize);
  const rangeStart = (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, effectiveTotalCount);

  // The list can shrink underneath the current page (draft deletions, or
  // navigating to a smaller group), which would otherwise leave the user on an
  // out-of-range page rendering no rows. Guard on totalPages > 0 so an empty
  // list doesn't clamp to page 0.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const renderStatusBadges = (row: TableRow) => {
    const badges: React.ReactNode[] = [];

    if (row.draftStatus === 'deleted') {
      badges.push(
        <Badge key="deleted" color="red">
          Deleted
        </Badge>,
      );
    } else if (row.draftStatus === 'added') {
      badges.push(
        <Badge key="new" color="blue">
          New
        </Badge>,
      );
    } else {
      badges.push(
        <Badge key="published" color="green">
          ● Completed
        </Badge>,
      );
      if (row.draftStatus === 'edited') {
        badges.push(
          <Badge key="modified" color="grey">
            Modified
          </Badge>,
        );
      }
    }

    return (
      <SpaceBetween direction="horizontal" size="xxs">
        {badges}
      </SpaceBetween>
    );
  };

  /**
   * Link to a string's detail page, or to one of its variants.
   *
   * Mirrors `VariantsTable.buildVariantHref`'s `?variant=` shape so the two
   * entry points into the same page agree. No `?version=` is forwarded: this
   * page has no pinned-version concept — it always reads the group's latest.
   */
  const buildStringHref = (stringId: string, variantCxId?: string): string => {
    const path = `/${encodeURIComponent(namespaceId)}/${encodeURIComponent(stringGroupId)}/${encodeURIComponent(stringId)}`;
    if (variantCxId == null) {
      return path;
    }
    const query = new URLSearchParams();
    query.set('variant', variantCxId);
    return `${path}?${query.toString()}`;
  };

  const columnDefinitions: TableProps.ColumnDefinition<TableRow>[] = [
    {
      id: 'stringId',
      header: 'String ID',
      sortingField: 'stringId',
      minWidth: 180,
      cell: (row) => {
        if (row.isVariant) {
          // Per-variant decision, precomputed on the row: only a variant the
          // server actually holds has a detail page worth linking to.
          const badge = <Badge color="blue">Variant: {row.variantCxId}</Badge>;
          return (
            <span className={styles.variantCellIndent}>
              {row.isVariantLinkable ? (
                <Link
                  href={buildStringHref(
                    row.stringInfo.stringId,
                    row.variantCxId,
                  )}
                  data-testid="variant-row-link"
                >
                  {badge}
                </Link>
              ) : (
                badge
              )}
            </span>
          );
        }
        // A draft-added string does not exist on the server yet, so its detail
        // page would 404 — that row stays unlinked. A draft-DELETED string still
        // exists and is viewable, so it keeps its link.
        const isDraftAdded = row.draftStatus === 'added';
        const hasVariants = row.stringInfo.variants.length > 0;
        const isExpanded = expandedStringIds.has(row.stringInfo.stringId);
        const isDeleted = row.draftStatus === 'deleted';
        const label = isDeleted ? (
          <span className={styles.deletedText}>{row.stringInfo.stringId}</span>
        ) : (
          row.stringInfo.stringId
        );
        return (
          // The expand toggle is a sibling of the link, not its parent or child,
          // so neither swallows the other's click.
          <SpaceBetween direction="horizontal" size="xxs">
            {hasVariants && (
              <button
                className={styles.expandToggle}
                onClick={() => toggleExpand(row.stringInfo.stringId)}
                aria-label={
                  isExpanded
                    ? `Collapse variants for ${row.stringInfo.stringId}`
                    : `Expand variants for ${row.stringInfo.stringId}`
                }
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            {isDraftAdded ? (
              <span data-testid="string-id-plain">
                {row.stringInfo.stringId}
              </span>
            ) : (
              <Link
                href={buildStringHref(row.stringInfo.stringId)}
                data-testid="string-id-link"
              >
                {label}
              </Link>
            )}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      sortingField: 'status',
      cell: (row) => renderStatusBadges(row),
    },
    {
      id: 'sourceText',
      header: 'Source Text',
      sortingField: 'sourceText',
      minWidth: 180,
      cell: (row) => {
        const text = row.isVariant
          ? row.variantSourceText
          : row.stringInfo.sourceText;
        const isDeleted = row.draftStatus === 'deleted';
        return isDeleted ? (
          <span className={styles.deletedText}>{text}</span>
        ) : (
          text
        );
      },
    },
    {
      id: 'contextNote',
      header: 'Context Note',
      minWidth: 180,
      cell: (row) =>
        row.isVariant
          ? (row.variantContextNote ?? '—')
          : (row.stringInfo.contextNote ?? '—'),
    },
    {
      id: 'actions',
      header: 'Actions',
      minWidth: 140,
      cell: (row) => {
        if (row.draftStatus === 'deleted' || row.isVariant) {
          return null;
        }
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {permissionsLoading ? (
              <Skeleton display="inline-block" height="32px" width="60px" />
            ) : (
              <Button
                disabled={!canEdit}
                onClick={() => onEditClick?.(row.stringInfo)}
              >
                Edit
              </Button>
            )}
          </SpaceBetween>
        );
      },
    },
  ];

  return (
    <Table
      items={tableRows}
      columnDefinitions={columnDefinitions}
      columnDisplay={columnDisplay}
      preferences={
        <TablePreferences
          config={STRINGS_TABLE_PREFERENCES}
          preferences={preferences}
          onConfirm={onConfirm}
        />
      }
      variant="container"
      wrapLines={preferences.wrapLines}
      stripedRows={preferences.stripedRows}
      contentDensity={preferences.contentDensity}
      trackBy="id"
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={handleSortingChange}
      header={
        <Header
          counter={`(${effectiveTotalCount})`}
          info={
            <span className={styles.paginationLabel}>
              ℹ️ Max {MAX_STRINGS_PER_GROUP} strings can be added to this group
            </span>
          }
          actions={
            permissionsLoading ? (
              <>
                <Skeleton display="inline-block" height="32px" width="120px" />
              </>
            ) : (
              <Button
                variant="primary"
                disabled={!canEdit}
                onClick={onCreateClick}
              >
                Create String
              </Button>
            )
          }
        >
          Strings
        </Header>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPage}
          pagesCount={totalPages}
          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          ariaLabels={{
            nextPageLabel: 'Next page',
            previousPageLabel: 'Previous page',
            pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
          }}
        />
      }
      footer={
        <span className={styles.paginationLabel}>
          Showing {rangeStart}–{rangeEnd} of {effectiveTotalCount} strings
        </span>
      }
    />
  );
};

export default StringsTable;
